import classNames from 'classnames';
import { AnimatePresence } from 'framer-motion';
import type { Op } from 'quill/core';
import React, { useEffect, useState } from 'react';
import { useAppDispatch } from '../../app/redux/useAppSelector';
import { MenuItem, MenuSeparator } from '../../components/Menu';
import OpacityMotionTransition from '../../components/OpacityMotionTransition';
import Tooltip from '../../components/Tooltip';
import PopOverContainer from '../../components/deprecated/PopOverContainer';
import { CREATION_ID, EditorActions } from '../../editor/editorSlice';
import type { DocumentType } from '../../graphql/generated/gql';
import { gql } from '../../graphql/generated/gql';
import editIcon from '../../icons/non-prefilled/16x16/editPost.svg';
import linkIcon from '../../icons/non-prefilled/16x16/link.svg';
import trashIcon from '../../icons/non-prefilled/16x16/trash.svg';
import { UIActions } from '../../ui/uiSlice';
import addTestId from '../../utils/addTestId';
import useRemoveHash from '../../utils/router/useRemoveHash';
import annotationThreadHash from '../../utils/url/annotationThreadHash';
import fullOrgUrl from '../../utils/url/fullOrgUrl';
import postPath from '../../utils/url/postPath';
import { sanitizeAnnotationLink } from '../../utils/url/sanitizeUrl';
import type { MagneticWallItemSize } from '../MagneticWallItem';
import MagneticWallItem, { MagneticWallItemCard } from '../MagneticWallItem';
import { usePostLayoutSize } from '../PostLayoutProvider';
import MagneticWallItemOverflowMenu, {
  MagneticWallOverflowButton,
} from '../comment/MagneticWallItemOverflowMenu';
import showFullPageSheet from '../comment/showFullPageSheet';
import isContentEmpty from '../utils/isContentEmpty';
import styles from './Annotation.module.scss';
import AnnotationCreate from './AnnotationCreate';
import AnnotationIcon, { AnnotationIconContainer } from './AnnotationIcon';
import AnnotationLink from './AnnotationLink';
import AnnotationPreview from './AnnotationPreview';
import useDeleteAnnotation from './useDeleteAnnotation';
import useShareSecret from '../useShareSecret';

const POST_FRAGMENT = gql(`
  fragment Annotation_post on RootPostFields {
    id
    ...postPath_post
  }
`);

const ANNOTATION_FRAGMENT = gql(`
  fragment Annotation_annotation on PublicAnnotationFields {
    id
    linkText
    ...AnnotationPreview_annotation
  }
`);

interface AnnotationCompactProps {
  onClick: () => void;
  active: boolean;
}

const AnnotationCompact = ({ onClick, active }: AnnotationCompactProps) => {
  const postLayoutSize = usePostLayoutSize();

  const isTabletIcon = postLayoutSize === 'tablet';

  return (
    // eslint-disable-next-line jsx-a11y/no-static-element-interactions -- TODO - fix next time this file is edited
    <div
      {...addTestId('annotation-icon')}
      className={classNames({
        [styles.compactAnnotationIconContainer]: !isTabletIcon,
        [styles.ring]: !isTabletIcon && active && showFullPageSheet(),
      })}
      onClick={onClick}
    >
      {isTabletIcon ? (
        <AnnotationIcon />
      ) : (
        <AnnotationIconContainer hoverable={!active}>
          <AnnotationIcon />
        </AnnotationIconContainer>
      )}
    </div>
  );
};

interface AnnotationProps {
  active: boolean;
  minimized: boolean;
  annotation: DocumentType<typeof ANNOTATION_FRAGMENT>;
  post: DocumentType<typeof POST_FRAGMENT>;
  onResize?: () => void;
  unsavedInputValue?: Op[];
  onInputChange: (content: Op[]) => void;
  popOverOpen: boolean;
  size: MagneticWallItemSize;
  canEdit: boolean;
  widerLineMargin: number;
}

const Annotation = ({
  active,
  minimized,
  annotation,
  post,
  onResize,
  unsavedInputValue = [],
  onInputChange,
  popOverOpen,
  size,
  canEdit,
  widerLineMargin,
}: AnnotationProps) => {
  const shareSecret = useShareSecret();
  const isNewAnnotation = annotation.id === CREATION_ID;
  const dispatch = useAppDispatch();

  const [linkText, setLinkText] = useState(annotation.linkText ?? '');
  const trimmedLinkText = linkText.trim();
  const [link, setLink] = useState(annotation.link ?? '');
  const sanitizedLink = sanitizeAnnotationLink(link);

  const [isEditing, setIsEditing] = useState(isNewAnnotation);
  const [isHovered, setIsHovered] = useState(false);

  useEffect(() => {
    onResize?.();

    if (!active) {
      setIsEditing(false);
    }
  }, [onResize, active, isHovered]);

  useEffect(() => {
    if (isEditing) {
      setLinkText(annotation.linkText ?? '');
      setLink(annotation.link ?? '');
    }
  }, [isEditing, annotation]);

  useEffect(() => {
    if (isHovered) {
      dispatch(EditorActions.highlightAnnotation(annotation.id));
    } else {
      dispatch(EditorActions.removeMagneticWallHighlight());
    }
  }, [dispatch, annotation.id, isHovered]);

  const onDismiss = () => {
    dispatch(EditorActions.blurMagneticWall());
  };

  const focusAnnotationById = (id: string) => {
    dispatch(
      EditorActions.focusAnnotation({
        id,
        isActive: true,
        hasExpandIntent: false,
      }),
    );
  };

  const focusCurrentAnnotation = () => focusAnnotationById(annotation.id);

  const deleteAnnotation = useDeleteAnnotation();
  const removeHash = useRemoveHash();

  const onDelete = async () => {
    await deleteAnnotation(annotation.id);

    if (window.location.hash === annotationThreadHash(annotation.id)) {
      removeHash();
    }
    onDismiss();
  };

  const shouldShowLink =
    (isEditing || !isContentEmpty(annotation.content)) && sanitizedLink;

  const innerFullView = (
    <div className={styles.innerFullViewContainer}>
      {isEditing ? (
        <div className={styles.createContainer}>
          <AnnotationCreate
            annotation={annotation}
            initialContent={
              isNewAnnotation
                ? unsavedInputValue
                : annotation.content ?? unsavedInputValue
            }
            onChange={onInputChange}
            postId={post.id}
            onSaved={(savedAnnotation) => {
              focusAnnotationById(savedAnnotation.id);
              onInputChange?.([]);
              setIsEditing(false);
            }}
            link={link}
            onChangeLink={setLink}
            linkText={linkText}
            onChangeLinkText={setLinkText}
            onCancel={() => setIsEditing(false)}
          />
        </div>
      ) : (
        <div className={styles.previewContainer}>
          {/* eslint-disable-next-line jsx-a11y/no-static-element-interactions */}
          <div
            onClick={() => focusCurrentAnnotation()}
            className={styles.preview}
          >
            <AnnotationPreview annotation={annotation} />
          </div>
        </div>
      )}
      {isHovered && !isEditing && (
        <MagneticWallItemOverflowMenu
          menuItems={
            canEdit
              ? [
                  <MenuItem
                    icon={editIcon}
                    key="edit"
                    onClick={() => {
                      focusCurrentAnnotation();
                      setIsEditing(true);
                    }}
                    testId="edit-annotation-button"
                  >
                    Edit annotation
                  </MenuItem>,
                  <MenuSeparator key="separator" />,
                  <MenuItem
                    icon={trashIcon}
                    key="delete"
                    danger
                    onClick={onDelete}
                  >
                    Delete annotation
                  </MenuItem>,
                ]
              : []
          }
        >
          <Tooltip content={`Copy link\nto annotation`}>
            <MagneticWallOverflowButton
              onClick={() => {
                const url = fullOrgUrl(
                  postPath(post, {
                    shareSecret,
                    anchor: annotationThreadHash(annotation.id),
                  }),
                );

                navigator.clipboard.writeText(url).then(() => {
                  dispatch(
                    UIActions.pushToast({
                      level: 'success',
                      message: 'Link copied to clipboard',
                    }),
                  );
                });
              }}
              icon={linkIcon}
            />
          </Tooltip>
        </MagneticWallItemOverflowMenu>
      )}
    </div>
  );

  return (
    <MagneticWallItem
      minimized={minimized}
      focused={active}
      isPopOverOpen={popOverOpen}
      id={`annotation-${annotation.id}`}
      onBlur={onDismiss}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      widerLineMargin={widerLineMargin}
      popOverContent={
        <PopOverContainer noPadding width="auto">
          <div className={styles.popOverInnerContainer}>
            {innerFullView}
            {shouldShowLink && (
              <div className={styles.annotationLinkContainer}>
                <AnnotationLink link={sanitizedLink} text={trimmedLinkText} />
              </div>
            )}
          </div>
        </PopOverContainer>
      }
      fullView={
        <MagneticWallItemCard focused={active}>
          <AnimatePresence>
            {active ? (
              <>{innerFullView}</>
            ) : (
              <div className={styles.fullAnnotationIconContainer}>
                {/* eslint-disable-next-line jsx-a11y/no-static-element-interactions */}
                <div
                  className={styles.icon}
                  {...addTestId('annotation-icon')}
                  onClick={() => focusCurrentAnnotation()}
                >
                  <AnnotationIconContainer hoverable>
                    <AnnotationIcon />
                  </AnnotationIconContainer>
                </div>
              </div>
            )}
            {active && shouldShowLink && (
              <OpacityMotionTransition
                key="link"
                disabled={isEditing}
                transition={{ duration: 0.25 }}
              >
                <AnnotationLink link={sanitizedLink} text={trimmedLinkText} />
              </OpacityMotionTransition>
            )}
          </AnimatePresence>
        </MagneticWallItemCard>
      }
      compactView={
        <AnnotationCompact
          active={active}
          onClick={() => focusCurrentAnnotation()}
        />
      }
      size={size}
    />
  );
};

export default Annotation;
